import {Exclude, Expose, instanceToPlain, plainToInstance} from "class-transformer";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON, validateJSONNow} from "../web/validation";
import {Entity} from "./entity";
import {PersonEntity} from "./person";

export const MessageValidator = t.intersection([
  t.type({
    id: t.string,
    objectId: t.string,
    objectType: t.string,
    messageType: t.string,
    message: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    report: t.boolean,
    personId: t.string,
    personName: t.string,
    action: t.string,
    regionIds: t.array(t.string),
  }),
]);

export const MessageArrayValidator = t.array(MessageValidator);

export type Message = t.TypeOf<typeof MessageValidator>;
export type MessageArray = t.TypeOf<typeof MessageArrayValidator>;

@Exclude()
export class MessageEntity implements Entity {
  @Expose() id!: string;
  @Expose() objectId!: string;
  @Expose() objectType!: string;
  @Expose() messageType!: string;
  @Expose() message!: string;
  @Expose() report?: boolean;
  @Expose() regionIds?: string[];
  @Expose() personId?: string;
  @Expose() personName?: string;
  @Expose() createDttm?: number;
  @Expose() lastModifiedDttm?: number;

  readonly entityType = 'MESSAGE';

  get displayName(): Readonly<string> {
    return this.message;
  }

  /**
   * Constructor for MessageEntity. Recommend using the {@link create} or {@link builder} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static builder(id: string = uuid()): EntityBuilderStep {
    return new MessageBuilder(id);
  }

  /**
   * Create a DispatchRequestEntity.
   *
   * @param builder The dispatch request builder.
   * @param id (optional) The primary key of the dispatchRequestAssoc.
   */
  static create(builder: MessageBuilder): MessageEntity {
    this.checkValidity(builder);
    const entity = new MessageEntity();
    entity.id = builder.id;
    entity.objectId = builder.objectId;
    entity.objectType = builder.objectType;
    entity.messageType = builder.messageType;
    entity.message = builder.message;
    entity.report = builder.report;
    entity.personId = builder.createdBy?.id;
    entity.personName = builder.createdBy?.nameReverse;
    entity.createDttm = builder.createDttm;
    entity.lastModifiedDttm = builder.lastModifiedDttm;
    return entity;
  }

  /**
   * Checks the validity of the builder.
   *
   * @param builder The builder.
   * @throws an error if the required files are not set.
   */
  private static checkValidity(builder: MessageBuilder): void {
    const errors: string[] = [];
    const requiredKeys: (keyof MessageBuilder)[] = [
      'id',
      'message',
      'messageType',
      'objectId',
      'objectType',
    ];

    const keys = Object.keys(builder) as (keyof MessageBuilder)[];

    keys.forEach((key) => {
      if (requiredKeys.includes(key)) {
        if (builder[key] === undefined) {
          errors.push(key);
        }
      }
    });

    if (errors.length > 0) {
      throw Error(`The following fields must be defined: ${errors}`);
    }
  }

  /**
   * Create a MessageEntity from a Message type value.
   *
   * @param message the message type value.
   */
  static from(message: Message): MessageEntity {
    return plainToInstance(MessageEntity, message, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<Message> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(MessageValidator, object);
  }

  asTypeValue(): Message {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(MessageValidator, object);
  }
}

interface MessageTypeBuilderStep {
  setMessageType: (value: string) => MessageBuilderStep;
}

interface MessageBuilderStep {
  setMessage: (value: string) => MessageBuilder;
}

interface EntityBuilderStep {
  setEntity: (value: Entity) => MessageTypeBuilderStep;
}

export class MessageBuilder
  implements EntityBuilderStep,
    MessageTypeBuilderStep,
    MessageBuilderStep {
  id: string;
  createdBy?: PersonEntity;
  messageType!: string;
  message!: string;
  objectId!: string;
  objectType!: string;
  report?: boolean;
  createDttm?: number;
  lastModifiedDttm?: number;

  constructor(id: string | undefined = uuid()) {
    this.id = id;
  }

  setCreatedBy(value: PersonEntity): MessageBuilder {
    this.createdBy = value;
    return this;
  }

  setMessageType(value: string): MessageBuilderStep {
    this.messageType = value;
    return this;
  }

  setMessage(value: string): MessageBuilder {
    this.message = value;
    return this;
  }

  setEntity(value: Entity): MessageTypeBuilderStep {
    this.objectId = value.id;
    this.objectType = value.entityType;
    return this;
  }

  setReportable(value: boolean | undefined): MessageBuilder {
    this.report = value;
    return this;
  }

  setCreateDttm(value: number | undefined): MessageBuilder {
    this.createDttm = value;
    return this;
  }

  setLastModifiedDttm(value: number | undefined): MessageBuilder {
    this.lastModifiedDttm = value;
    return this;
  }

  build(): MessageEntity {
    return MessageEntity.create(this);
  }
}
